// 📅 Bandeau du prochain cours réservé (API GET /classes/upcoming) : ce qu'il faut afficher
// selon l'état du cours et le rôle (prof ou élève), en heure de Paris. Renvoie null (rien à
// afficher) ou { tone, text: [clé, valeurs], detail?: [clé, valeurs], actions }, avec actions
// parmi « join » (rejoindre la salle), « cancelLate » (élève : annuler, prof en retard de plus
// de 15 min), « markAbsent » (prof : clore en « élève absent »).
import { formatTime } from "./parisTime";
import { subjectOfItem } from "./subjectName";

const MIN = 60 * 1000;
// Cours programmé : bandeau affiché dans l'heure qui précède
export const SHOW_SCHEDULED_BEFORE_MINUTES = 60;

const minutesSince = (iso, now) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / MIN));

// Déjà dans la salle de ce cours (`inRoom`) : pas de « Rejoindre la salle », et plus de bandeau
// s'il ne disait que « salle ouverte » ou « en cours » ; les autres actions (annuler pour
// retard, élève absent) et les informations (prof encore dans son cours précédent) restent.
export const bannerOf = (c, now = Date.now(), { inRoom = false, language = "fr" } = {}) => {
  const view = viewOf(c, now, language);
  if (!view || !inRoom) return view;
  const actions = view.actions.filter((a) => a !== "join");
  if (!actions.length && !view.detail && view.tone === "success") return null;
  return { ...view, actions };
};

const viewOf = (c, now, language) => {
  if (!c) return null;
  const base = { subject: subjectOfItem(c, language), with: c.with, time: formatTime(c.scheduled_at) };
  const waiting = c.student_joined_at ? ["upcoming.studentWaiting", { with: c.with, minutes: minutesSince(c.student_joined_at, now) }] : null;

  if (c.status === 9) {
    if (new Date(c.scheduled_at).getTime() - now > SHOW_SCHEDULED_BEFORE_MINUTES * MIN) return null;
    return { tone: "info", text: ["upcoming.scheduled", { ...base, opens: formatTime(c.opens_at) }], actions: [] };
  }

  if (c.status === 2 && c.role === "student") {
    if (c.teacher_in_previous_class) {
      // Compte à rebours théorique jusqu'à la fin prévue du cours précédent du prof
      const left = Math.ceil((new Date(c.previous_planned_end).getTime() - now) / MIN);
      return {
        tone: "warning",
        text: left > 0 ? ["upcoming.teacherFinishing", { ...base, end: formatTime(c.previous_planned_end), minutes: left }] : ["upcoming.teacherOverrunning", { ...base, end: formatTime(c.previous_planned_end) }],
        detail: c.can_cancel_late ? ["upcoming.lateChoice", { minutes: c.late_minutes }] : null,
        actions: c.can_cancel_late ? ["join", "cancelLate"] : ["join"],
      };
    }
    if (c.can_cancel_late) return { tone: "warning", text: ["upcoming.teacherLate", { ...base, minutes: c.late_minutes }], actions: ["join", "cancelLate"] };
    return { tone: "success", text: ["upcoming.openStudent", base], actions: ["join"] };
  }

  if (c.status === 2) {
    // Prof encore dans son cours précédent : le cours suivant ne peut pas démarrer
    if (c.teacher_in_previous_class) return { tone: "warning", text: ["upcoming.teacherNext", base], detail: waiting || ["upcoming.finishFirst", {}], actions: [] };
    return { tone: "success", text: ["upcoming.openTeacher", base], detail: waiting, actions: ["join"] };
  }

  if (c.status === 6 && c.role === "teacher" && c.student_absent_from) {
    const absentNow = now >= new Date(c.student_absent_from).getTime();
    return {
      tone: absentNow ? "warning" : "info",
      text: ["upcoming.waitingStudent", base],
      detail: absentNow ? null : ["upcoming.absentFrom", { time: formatTime(c.student_absent_from) }],
      actions: absentNow ? ["join", "markAbsent"] : ["join"],
    };
  }
  if (c.status === 6) return { tone: "success", text: ["upcoming.ongoing", base], actions: ["join"] };
  return null;
};
